import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PriceSummary } from "../components/PriceSummary";
import api, { apiError, getDraft, setDraft, AED } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Tag, ShieldCheck, Plus, Minus, Truck, Store, CalendarDays, FileText, Plane, Undo2, LifeBuoy, PhoneCall } from "lucide-react";
import dayjs from "dayjs";

const EXTRA_BENEFITS = {
  SCDW: "Zero excess on own-damage claims, so you drive with total peace of mind.",
  PAI: "Personal accident cover for you and every passenger.",
  CS: "Safety-certified seats for every age, required by UAE law under 4.",
  ADD_DRV: "Share the driving on long trips, registered and insured properly.",
};

const slotHourFrom = (iso) => {
  const d = dayjs(iso);
  return d.hour() + (d.minute() >= 30 ? 0.5 : 0);
};

export default function Booking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draft] = useState(() => getDraft());
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [extras, setExtras] = useState({});
  const [payType, setPayType] = useState("later");
  const [ndw, setNdw] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoCode, setPromoCode] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!draft?.vehicle) {
      navigate("/");
      return;
    }
    api.get("/extras").then((r) => setExtrasCatalog([...r.data].sort((a, b) => (a.code === "SCDW" ? -1 : b.code === "SCDW" ? 1 : 0)))).catch(() => {});
  }, [draft, navigate]);

  const quotePayload = useMemo(() => {
    if (!draft?.vehicle) return null;
    const { search, handover } = draft;
    const isDeliveryPickup = handover.type === "delivery";
    const collect = handover.returnType === "collect";
    const deliveryEnabled = isDeliveryPickup || collect;
    const deliveryEmirate = isDeliveryPickup ? handover.emirate : (handover.returnEmirate || handover.emirate);
    const deliveryAnchor = isDeliveryPickup ? search.pickup_datetime : (search.return_datetime || search.pickup_datetime);
    return {
      vehicle_id: draft.vehicle.id,
      mode: search.mode,
      pickup_station: isDeliveryPickup ? null : handover.branchId,
      dropoff_station: handover.returnType === "branch" ? handover.returnBranchId : null,
      pickup_datetime: search.pickup_datetime,
      return_datetime: search.mode === "monthly" ? null : search.return_datetime,
      months: search.months,
      model_year: search.model_year || 2025,
      mileage_package: search.mileage_package || 3000,
      driver_age: 25,
      extras: Object.entries(extras).filter(([, q]) => q > 0).map(([code, qty]) => ({ code, qty })),
      pay_type: payType,
      promo_code: promoCode,
      ndw,
      delivery: deliveryEnabled ? {
        enabled: true,
        emirate: deliveryEmirate,
        date: deliveryAnchor.slice(0, 10),
        hour: slotHourFrom(deliveryAnchor),
        return_to: handover.returnType === "branch" ? handover.returnBranchId : "delivery_location",
      } : null,
    };
  }, [draft, extras, payType, promoCode, ndw]);

  const payloadKey = useMemo(() => (quotePayload ? JSON.stringify(quotePayload) : null), [quotePayload]);

  useEffect(() => {
    if (!payloadKey) return;
    setQuoteLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post("/quote", JSON.parse(payloadKey))
        .then((r) => setQuote(r.data))
        .catch((e) => {
          toast.error(apiError(e));
          if (JSON.parse(payloadKey).promo_code) setPromoCode(null);
          else setQuote(null);
        })
        .finally(() => setQuoteLoading(false));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [payloadKey]);

  if (!draft?.vehicle) return null;
  const { vehicle, search, handover, flight } = draft;
  const isDeliveryPickup = handover.type === "delivery";
  const isCollect = handover.returnType === "collect";
  const showDeliveryInfo = isDeliveryPickup || isCollect;
  const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const returnPlace = cap(isDeliveryPickup ? handover.emirate : (handover.returnEmirate || handover.emirate));
  const isMonthly = search.mode === "monthly";
  const ndwFee = vehicle.insurance_tier === "premium"
    ? (isMonthly ? "AED 410/mo" : "AED 50/day · 260/wk")
    : (isMonthly ? "AED 210/mo" : "AED 35/day · 160/wk");

  const setQty = (code, delta, max) =>
    setExtras((e) => ({ ...e, [code]: Math.max(0, Math.min((e[code] || 0) + delta, max)) }));

  const applyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoCode(code);
  };

  const proceed = () => {
    if (!quote || quoteLoading) {
      toast.error(t("Please wait for your price to update"));
      return;
    }
    setDraft({ ...draft, selections: quotePayload, quote });
    navigate(user ? "/payment" : "/login?next=/payment");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full flex-1 grid lg:grid-cols-[1fr_360px] gap-8" data-testid="booking-review-page">
        <div className="space-y-6">
          {/* Locked-in vehicle */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-4 flex flex-wrap items-center gap-4" data-testid="locked-vehicle-card">
            <img src={vehicle.image} alt={vehicle.name} className="w-28 h-20 object-cover rounded" />
            <div className="flex-1 min-w-[180px]">
              <span className="text-[10px] uppercase tracking-widest text-[#57685e]">{vehicle.category} · {t("locked in for you")}</span>
              <h1 className="text-lg font-bold text-[#0F1E17]">{vehicle.name} <span className="text-xs font-normal text-[#57685e]">{t("or similar")}</span></h1>
              <div className="text-xs text-[#57685e] mt-1 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-[#009900]" />
                  {dayjs(search.pickup_datetime).format("DD MMM HH:mm")} → {dayjs(search.return_datetime).format("DD MMM HH:mm")}
                  {isMonthly && ` ${t("({{n}} months)", { n: search.months })}`}
                </span>
                <span className="flex items-center gap-1">
                  {isDeliveryPickup ? <><Truck className="w-3.5 h-3.5 text-[#009900]" /> {t("Delivered in {{place}}", { place: cap(handover.emirate) })}</> : <><Store className="w-3.5 h-3.5 text-[#009900]" /> {search.pickup_station?.name}</>}
                </span>
                <span className="flex items-center gap-1" data-testid="return-summary">
                  {handover.returnType === "collect" ? <><Undo2 className="w-3.5 h-3.5 text-[#009900]" /> {t("Collected from {{place}}", { place: returnPlace })}</> : <><Store className="w-3.5 h-3.5 text-[#009900]" /> {t("Returned to branch")}</>}
                </span>
                {flight?.number && <span className="flex items-center gap-1"><Plane className="w-3.5 h-3.5 text-[#009900]" /> {flight.number}</span>}
              </div>
            </div>
          </div>

          {/* Add-ons with benefit copy */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-1">{t("Make it yours")}</h3>
            <p className="text-xs text-[#57685e] mb-4" data-testid="addons-intro">
              {t("Every rental already includes CDW and third-party cover over 1 million USD. These extras take the last bit of worry off the road, and most drivers add at least one.")}
            </p>
            {(!extras.SCDW || !ndw) && (
              <div className="mb-4 rounded-lg border border-[#FFDC00] bg-[#FFF9DC]/50 p-3" data-testid="addon-upsell">
                <div className="text-xs font-semibold text-[#144733] mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#009900]" /> {t("Recommended for a worry-free trip")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {!extras.SCDW && (
                    <button type="button" onClick={() => setQty("SCDW", 1, 1)} data-testid="upsell-add-scdw"
                      className="text-xs font-semibold rounded-full border border-[#009900] text-[#009900] px-3 py-1.5 hover:bg-[#f0f9f0] transition-colors">
                      {t("+ Zero excess with Super CDW")}
                    </button>
                  )}
                  {!ndw && (
                    <button type="button" onClick={() => setNdw(true)} data-testid="upsell-add-ndw"
                      className="text-xs font-semibold rounded-full border border-[#009900] text-[#009900] px-3 py-1.5 hover:bg-[#f0f9f0] transition-colors">
                      {t("+ No deposit blocked on your card")}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              {extrasCatalog.map((e) => {
                const qty = extras[e.code] || 0;
                const recommended = e.code === "SCDW";
                return (
                  <div key={e.code} className={`border rounded-lg p-3 transition-colors relative ${qty > 0 ? "border-[#009900] bg-[#f0f9f0]" : recommended ? "border-[#009900]/50" : "border-[#e2ebe4]"}`} data-testid={`extra-${e.code}`}>
                    {recommended && (
                      <span className="absolute -top-2.5 left-3 bg-[#FFDC00] text-[#0F1E17] text-[9px] uppercase tracking-wide font-bold px-2 py-0.5 rounded" data-testid="scdw-recommended-badge">
                        {t("Recommended · Most added")}
                      </span>
                    )}
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="text-sm font-semibold text-[#0F1E17]">{e.name}</div>
                        <div className="text-[11px] text-[#57685e] mt-0.5">{EXTRA_BENEFITS[e.code] ? t(EXTRA_BENEFITS[e.code]) : e.description}</div>
                        <div className="text-xs font-semibold text-[#009900] mt-1">{e.price_notes?.[vehicle.insurance_tier] || e.price_note}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setQty(e.code, -1, e.max_qty)} disabled={qty === 0}
                          className="w-7 h-7 rounded border border-[#d5ddd5] flex items-center justify-center disabled:opacity-30" data-testid={`extra-minus-${e.code}`}>
                          <Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm font-bold w-4 text-center" data-testid={`extra-qty-${e.code}`}>{qty}</span>
                        <button onClick={() => setQty(e.code, 1, e.max_qty)} disabled={qty >= e.max_qty}
                          className="w-7 h-7 rounded border border-[#d5ddd5] flex items-center justify-center disabled:opacity-30" data-testid={`extra-plus-${e.code}`}>
                          <Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* NDW */}
          <div className={`bg-white rounded-lg border p-5 ${ndw ? "border-[#009900]" : "border-[#e2ebe4]"}`} data-testid="ndw-card">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={ndw} onChange={(e) => setNdw(e.target.checked)} data-testid="ndw-checkbox" />
              <div>
                <div className="text-sm font-semibold text-[#0F1E17] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#009900]" /> {t("Rent with No Deposit or Security blocking, {{fee}}", { fee: t(ndwFee) })}</div>
                <p className="text-xs text-[#57685e] mt-1">{t("Skip the {{amount}} credit-card pre-authorization entirely. Your card limit stays yours.", { amount: AED(vehicle.deposit_cc) })}</p>
              </div>
            </label>
          </div>

          {/* Payment preference + promo */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-4">{t("Payment preference")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setPayType("now")}
                className={`rounded-lg border-2 p-3 text-left ${payType === "now" ? "border-[#009900] bg-[#f0f9f0]" : "border-[#d5ddd5]"}`} data-testid="pay-now-btn">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#144733]">{t("Pay Now")}</div>
                <div className="text-[11px] text-[#009900] font-semibold">{t("Save 5% when you pay online today")}</div>
              </button>
              <button type="button" onClick={() => setPayType("later")}
                className={`rounded-lg border-2 p-3 text-left ${payType === "later" ? "border-[#009900] bg-[#f0f9f0]" : "border-[#d5ddd5]"}`} data-testid="pay-later-btn">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#144733]">{t("Pay Later at counter")}</div>
                <div className="text-[11px] text-[#57685e] font-semibold">{t("Standard price, pay when you collect")}</div>
              </button>
            </div>
            <div className="mt-4 flex gap-2">
              <div className="relative flex-1">
                <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57685e]" />
                <input className="field-input pl-9 uppercase" placeholder={t("Promo code")} value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)} data-testid="promo-code-input" />
              </div>
              <button onClick={applyPromo} className="border-2 border-[#009900] text-[#009900] font-medium uppercase text-xs tracking-wide rounded px-4 hover:bg-[#f0f9f0] transition-colors" data-testid="apply-promo-btn">
                {t("Apply")}
              </button>
            </div>
          </div>

          {/* T&C summary + documents needed */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5" data-testid="terms-docs-summary">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> {t("Before you pay: the essentials")}</h3>
            <ul className="text-xs text-[#57685e] space-y-1.5 list-disc pl-4">
              <li>{t("Minimum age 21 (25 for premium vehicles) · valid license required · \"or similar\" model policy.")}</li>
              <li>{t("Daily & weekly: unlimited km within UAE · Monthly: from 3,000 km included · excess mileage AED {{rate}}/km + VAT.", { rate: vehicle.insurance_tier === "premium" ? "0.75" : "0.50" })}</li>
              <li>{t("Fuel: return at the same level. Tolls (Salik in Dubai, Darb in Abu Dhabi) are billed after your rental + 25% admin fee; fines + 10% admin fee + VAT.")}</li>
              <li>{ndw ? t("No deposit, waived by your No-Deposit Waiver.") : t("Security deposit at handover: {{cc}} credit-card pre-auth or {{dc}} debit-card charge (refunded within 21 working days).", { cc: AED(vehicle.deposit_cc), dc: AED(vehicle.deposit_dc) })}</li>
              {showDeliveryInfo && <li>{t("Doorstep delivery & collection: from AED 100 + VAT per trip (AED 200 + VAT in RAK, UAQ & Fujairah); monthly delivery is free in Dubai & Sharjah.")}</li>}
              {showDeliveryInfo && <li>{t("Delivery address details are taken at checkout, on the next step.")}</li>}
            </ul>
            <div className="mt-3 bg-[#f4f7f4] rounded p-3 text-xs text-[#144733]">
              <strong>{t("Documents you'll upload after booking (2 minutes):")}</strong>{t(" an identification document (passport, national ID or Emirates ID) and your driver's licence (or International Driving Permit). Camera or file upload, JPEG/PNG/PDF up to 5 MB. We need them to verify the driver before handover.")}
            </div>
            <p className="mt-3 text-xs text-[#57685e]">
              {t("Plans might change? Read our")}{" "}
              <Link to="/modify-reservation" className="text-[#009900] underline" data-testid="booking-modify-link">{t("cancellation & modification rules")}</Link>
              {t(". Free cancellation up to 24 hours before pickup applies to Pay Now bookings.")}
            </p>
          </div>

          {/* Assist reassurance */}
          <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-lg p-4 grid sm:grid-cols-3 gap-4 text-xs text-[#144733]" data-testid="booking-assist-strip">
            <div className="flex gap-2.5">
              <Undo2 className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" />
              <span><strong>{t("Replacement car")}</strong>{t(" if you break down, we bring another car to you, free.")}</span>
            </div>
            <div className="flex gap-2.5">
              <LifeBuoy className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" />
              <span><strong>{t("24/7 roadside")}</strong>{t(" anywhere in the UAE, included on every rental.")}</span>
            </div>
            <div className="flex gap-2.5">
              <PhoneCall className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" />
              <span><strong>{t("Real people on call")}</strong>{t(" on 800 EUROPCAR, before, during and after your rental.")}</span>
            </div>
          </div>
        </div>

        {/* Sticky summary */}
        <aside className="hidden lg:block">
          <div className="sticky top-20">
            <PriceSummary quote={quote} loading={quoteLoading} ctaLabel={payType === "later" ? t("Proceed to Check-Out") : t("Proceed with Payment")} onCta={proceed} />
          </div>
        </aside>
      </main>

      {quote && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#e2ebe4] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] p-4 flex items-center justify-between gap-4 z-40" data-testid="mobile-summary-bar">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#57685e]">{t("Total incl. VAT")}</div>
            <div className="text-xl font-bold text-[#0F1E17]">{AED(quote.total)}</div>
          </div>
          <button onClick={proceed} className="btn-cta rounded px-6 py-3 text-xs" data-testid="mobile-proceed-btn">{payType === "later" ? t("Check-Out") : t("Pay Now")}</button>
        </div>
      )}
      <Footer />
    </div>
  );
}
